import { FC } from 'react'
import { IconPaths, PrescricaoType } from '_metronic/helpers'
import { ValoresItem, ItemPrescricao, rotuloCatalogo, selosDoItem } from '../../core/montagem/itens'
import { CampoPendente } from '../../core/montagem/pendencias'
import styles from '../../Prescricao.module.scss'
import { Icone } from '../comum/Icone'
import { SelosDoItem } from '../comum/Selos'
import { useRotulos } from '../comum/useRotulos'
import { CamposDoItem, ErrosDoItem } from './CamposDoItem'
import { Button } from 'app/components'

type IProps = {
  item: ItemPrescricao
  numero?: number
  aberto: boolean
  atendeSUS: boolean
  pendentes: CampoPendente[]
  /** Depois de abrir a revisão, o que falta vira erro (vermelho, ligado ao campo). */
  mostrarErros: boolean
  podeEditarCadastro: boolean
  onAlternar: () => void
  onRemover: () => void
  onChange: (campos: ValoresItem) => void
  onEditarCadastro: () => void
}

/**
 * Card de um item. Fechado mostra selos, nome, resumo e o que falta; aberto, os campos.
 * O cabeçalho é um botão com `aria-expanded`; remover é um botão só de ícone com nome completo.
 */
export const ItemCard: FC<IProps> = ({
  item,
  numero,
  aberto,
  atendeSUS,
  pendentes,
  mostrarErros,
  podeEditarCadastro,
  onAlternar,
  onRemover,
  onChange,
  onEditarCadastro,
}) => {
  const { t, rotuloCampo } = useRotulos()
  const ehOrientacao = item.categoria === PrescricaoType.Orientacoes
  const nome = ehOrientacao
    ? item.catalogo.nome || t('prescricao.documento.orientacao')
    : item.catalogo.nome
  const idPainel = `${item.chave}-painel`

  const erros: ErrosDoItem = mostrarErros
    ? Object.fromEntries(pendentes.map((campo) => [campo, t(`prescricao.erro.${campo}`)]))
    : {}

  const resumo = (() => {
    switch (item.categoria) {
      case PrescricaoType.Orientacoes:
        return item.texto || t('prescricao.item.sem_texto')
      case PrescricaoType.Exame:
        return [
          item.instrucoes || t('prescricao.item.sem_instrucoes'),
          item.observacoes && t('prescricao.item.obs', { texto: item.observacoes }),
        ]
          .filter(Boolean)
          .join(' · ')
      default: {
        return [
          item.posologia || t('prescricao.item.sem_posologia'),
          item.quantidade && t('prescricao.item.qtd', { texto: item.quantidade }),
          item.observacoes && t('prescricao.item.obs', { texto: item.observacoes }),
          item.categoria === PrescricaoType.Medicamento && item.usoContinuo
            ? t('prescricao.item.uso_continuo')
            : '',
        ]
          .filter(Boolean)
          .join(' · ')
      }
    }
  })()

  const classeBorda = aberto
    ? styles.itemAberto
    : mostrarErros && pendentes.length > 0
    ? styles.itemComErro
    : ''

  return (
    <article
      className={`${styles.item} ${classeBorda}`}
      aria-label={numero ? t('prescricao.item.aria', { numero, nome }) : nome}
    >
      <div className='d-flex gap-3 align-items-start p-4'>
        <Button
          id={`${item.chave}-cabecalho`}
          className={styles.itemCabecalho}
          ariaExpanded={aberto}
          // Só aponta para o painel quando ele existe (fechado, o painel não é renderizado).
          ariaControls={aberto ? idPainel : undefined}
          onClick={onAlternar}
          unstyled
        >
          <span className={`fw-bold ${styles.textoSecundario} ${styles.numeroItem}`}>
            {numero ? `${numero}.` : ''}
          </span>
          <span className={`flex-grow-1 d-flex flex-column gap-1 ${styles.encolhe}`}>
            <SelosDoItem selos={selosDoItem(item.catalogo, atendeSUS)} />
            <span>
              <strong>{nome}</strong>
              {!ehOrientacao && item.catalogo.concentracao && (
                <span className={styles.textoSecundario}> {item.catalogo.concentracao}</span>
              )}
            </span>
            <span className={`fs-7 ${styles.resumo}`}>{resumo}</span>
            {pendentes.length > 0 && (
              <span
                className={`d-inline-flex align-items-center gap-2 fs-7 fw-bold ${
                  mostrarErros ? styles.pendenciaErro : styles.pendenciaAviso
                }`}
              >
                <Icone icone={IconPaths.Alert} />
                {t('prescricao.item.falta', {
                  campos: pendentes.map((campo) => rotuloCampo(campo)).join(', '),
                })}
              </span>
            )}
          </span>
          <Icone icone={IconPaths.ArrowDown} className={`${styles.chevron} fs-4`} />
        </Button>
        {podeEditarCadastro && (
          <Button
            className={`${styles.botaoIcone} ${styles.botaoIconeNeutro}`}
            onClick={onEditarCadastro}
            ariaLabel={t('prescricao.item.editar_aria', {
              nome: ehOrientacao ? nome : rotuloCatalogo(item.catalogo),
            })}
            title={t('prescricao.item.editar')}
            unstyled
          >
            <Icone icone={IconPaths.Pen} className='fs-4' />
          </Button>
        )}
        <Button
          className={styles.botaoIcone}
          onClick={onRemover}
          ariaLabel={t('prescricao.item.remover_aria', {
            nome: ehOrientacao ? nome : rotuloCatalogo(item.catalogo),
          })}
          title={t('prescricao.item.remover')}
          unstyled
        >
          <Icone icone={IconPaths.Trash} className='fs-4' />
        </Button>
      </div>

      {aberto && (
        <div id={idPainel} className={styles.painelItem}>
          <CamposDoItem item={item} atendeSUS={atendeSUS} erros={erros} onChange={onChange} />
        </div>
      )}
    </article>
  )
}
